import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { ArrowDown, ArrowUp, Button, Clear, ContextMenu, Item, MenuButton, MenuTab, MenuTabs } from 'basic-components';
import { globalActions } from 'primary-components';

import { FocusTrap } from 'focus-trap-react';
import { Else, If, Then, When } from 'react-if';

import classNames from 'classnames';
import './Basket.scss';

const FOCUS_TRAP_CONFIG = {
    clickOutsideDeactivates: true,
    allowOutsideClick: true,
  }
const Basket = ({ address, addToSheet, counterLabel, isBasketOpen, swapIsOpen, dataTabName, dataTab, settings, selectedItems, onClearSelected, onReset }) => {
  const dispatch = useDispatch(),
        selectedItemsLength = selectedItems.length,
        areItemsSelected = useMemo(() => selectedItemsLength > 0, [selectedItemsLength]),
        [isMenuOpen, setIsMenuOpen] = useState(false),
        onMenuChange = useCallback(({ open }) => setIsMenuOpen(open), []),
        onResetLocal = useCallback(() => {
          dispatch(globalActions.getToastsStore()).warning("All changes will be discarded. Do you want to proceed?", "Clear changes",{
            withCancel: false,
            confirmText: "Clear",
            confirmAction: () => {
              onReset()
            }
          })
        }, [dispatch, onReset]),
        onKeyDown = useCallback(e => {
          if ((e.key === "Enter" || e.key === " ")) {
            e.preventDefault()
            swapIsOpen()
          }
        }, [swapIsOpen])

  return (
    <FocusTrap 
        active={isBasketOpen}
        focusTrapOptions={FOCUS_TRAP_CONFIG}
    >
      <div className={classNames(
        "ng-office-app__authed__content__basket",
        { "ng-office-app__authed__content__basket--is-open": isBasketOpen },
        { "ng-office-app__authed__content__basket--is-selected": areItemsSelected },
        { "ng-office-app__authed__content__basket--is-edit": !!address }
      )}>
        <div className="ng-office-app__authed__content__basket__header">
          <div className="ng-office-app__authed__content__basket__header__left">
            <ContextMenu
                itemsSize="unset"
                menuButton={(
                  <MenuButton tabIndex={isBasketOpen ? 0 : -1}>
                    <div className="ng-office-app__authed__content__basket__header__left__counter">
                      <div className="ng-office-app__authed__content__basket__header__left__counter__label">
                        {`${selectedItemsLength} ${counterLabel}`}
                      </div>
                      <div className="ng-office-app__authed__content__basket__header__left__counter__arrow">
                        <If condition={isMenuOpen}>
                          <Then>
                            <ArrowUp
                                color={areItemsSelected ? "white-base" : "grey-dark"}
                                height={12}
                                isWithoutBorder
                                width={12}
                            />
                          </Then>
                          <Else>
                            <ArrowDown
                                color={areItemsSelected ? "white-base" : "grey-dark"}
                                height={12}
                                isWithoutBorder
                                width={12}
                            />
                          </Else>
                        </If>
                      </div>
                    </div>
                  </MenuButton>
                )}
                offsetY={8}
                onMenuChange={onMenuChange}
            >
              <Item
                  onClick={onClearSelected}
              >
                Clear selected
              </Item>
              <Item onClick={onResetLocal}>
                Clear and reset
              </Item>
            </ContextMenu>
          </div>
          <div className="ng-office-app__authed__content__basket__header__right">
            <Button
                disabled={selectedItemsLength === 0}
                onClick={addToSheet}
                size="small"
                tabIndex={isBasketOpen ? 0 : -1}
                variant="primary"
            >
              Add to sheet
            </Button>
            <div 
                className="ng-office-app__authed__content__basket__header__right__exit"
                onClick={swapIsOpen}
                onKeyDown={onKeyDown}
                tabIndex={isBasketOpen ? 0 : -1}
            >
              <Clear
                  color={areItemsSelected ? "white-base" : "blue-bright"}
                  height={12}
                  width={12}
              />
            </div>  
          </div>
        </div>
        <When condition={isBasketOpen}>
          {() => (
            <div className="ng-office-app__authed__content__basket__body">
              <MenuTabs>
                <MenuTab name={dataTabName}>
                  {dataTab}
                </MenuTab>
                <MenuTab name="SETTINGS">
                  {settings}
                </MenuTab>
              </MenuTabs>
            </div>
          )}
        </When>
      </div>
    </FocusTrap>
  )
}

Basket.defaultProps = {
  address: undefined,
  counterLabel: "selected",
  dataTabName: "DATA",
  selectedItems: [],
}
Basket.propTypes = {
  address: PropTypes.any,
  addToSheet: PropTypes.func.isRequired,
  counterLabel: PropTypes.string,
  dataTab: PropTypes.element.isRequired,
  dataTabName: PropTypes.string,
  isBasketOpen: PropTypes.bool.isRequired,
  onClearSelected: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  selectedItems: PropTypes.array,
  settings: PropTypes.element.isRequired,
  swapIsOpen: PropTypes.func.isRequired,
}

export default Basket;